import {
  AllFulfillmentTypesType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  InputCheckbox,
  InputContainer,
  InputLabel,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { Alert } from "~/components/alert/Alert";
import { ChatInput } from "~/components/chatInput/ChatInput";
import { OrderV2Dto } from "@sellar-io/api-v2/types";
import { OrderConfirmationFormType } from "../ConfirmOrderForm";
import { FieldErrors } from "react-hook-form";
import { getIsSwitchingBuyerOrderFromSellarDistribution } from "./lib/isSwitchingBuyerOrderFromSD";
import { getFulfillmentChangeReasonOptions } from "./lib/getFulfillmentChangeReasonOptions";
import { formatFulfillmentTypeLabel } from "./lib/formatFulfillmentTypeLabel";

type FulfillmentChangeReason = {
  reason: OrderV2Dto.OrderConfirmRequestType["fulfillmentChangeReason"];
  message: NonNullable<
    OrderV2Dto.OrderConfirmRequestType["fulfillmentChangeReasonMessage"]
  >;
  acknowledged: boolean;
};

type SellarDistributionFulfillmentChangeFormFragmentProps = {
  currentFulfillmentType: AllFulfillmentTypesType;
  newFulfillmentType: AllFulfillmentTypesType;
  initiator: OrderGetResponseType["initiator"];
  fulfillmentChangeReason: FulfillmentChangeReason;
  buyerName: string;
  errors: FieldErrors<
    Pick<
      OrderConfirmationFormType,
      | "fulfillmentChangeAcknowledged"
      | "fulfillmentChangeReason"
      | "fulfillmentChangeReasonMessage"
    >
  >;
  disabled?: boolean;
  onChange: (reason: FulfillmentChangeReason) => void;
};

export const SellarDistributionFulfillmentChangeFormFragment = ({
  currentFulfillmentType,
  newFulfillmentType,
  initiator,
  disabled,
  fulfillmentChangeReason,
  buyerName,
  errors,
  onChange,
}: SellarDistributionFulfillmentChangeFormFragmentProps) => {
  const isSwitchingBuyerOrderFromSellarDistribution =
    getIsSwitchingBuyerOrderFromSellarDistribution({
      order: { fulfillmentType: currentFulfillmentType, initiator },
      fulfillmentType: newFulfillmentType,
    });

  if (!isSwitchingBuyerOrderFromSellarDistribution) return null;

  const fulfillmentChangeReasonOptions = getFulfillmentChangeReasonOptions({
    newFulfillmentType,
  });

  const hasAcknowledgedFulfillmentChange = fulfillmentChangeReason.acknowledged;

  return (
    <div className="flex flex-col gap-4">
      <Alert
        type="warning"
        title="You're changing your customer's chosen delivery method"
        message={`${buyerName} chose Delivered by Sellar at checkout. They're expecting delivery on their scheduled delivery day, and this order may be consolidated with orders from other suppliers. Changing to ${formatFulfillmentTypeLabel(
          newFulfillmentType,
        )} removes this order from that delivery. Please confirm the change with ${buyerName} before continuing.`}
      />
      <div className="flex flex-col gap-2">
        <label className="flex gap-4 rounded-lg border border-indigo-600 bg-indigo-50 p-4">
          <InputCheckbox
            variant="default"
            checked={hasAcknowledgedFulfillmentChange}
            onChange={(event) => {
              onChange({
                message: "",
                reason: undefined,
                acknowledged: event.target.checked,
              });
            }}
            disabled={disabled}
          />
          <div className="flex flex-col">
            I've confirmed with {buyerName} that they agree to this delivery
            method change.
          </div>
        </label>
        {errors.fulfillmentChangeAcknowledged && (
          <p className="text-sm text-red-600">
            {errors.fulfillmentChangeAcknowledged.message}
          </p>
        )}
      </div>
      {hasAcknowledgedFulfillmentChange && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <InputLabel>Reason for change</InputLabel>
            <InputContainer>
              <InputSelect
                onChange={(event) => {
                  onChange({
                    ...fulfillmentChangeReason,
                    reason: event.target
                      .value as FulfillmentChangeReason["reason"],
                  });
                }}
                value={fulfillmentChangeReason.reason ?? ""}
                disabled={disabled}
              >
                <InputOption value="">Select a reason</InputOption>
                {fulfillmentChangeReasonOptions.map((reason) => (
                  <InputOption key={reason.value} value={reason.value}>
                    {reason.label}
                  </InputOption>
                ))}
              </InputSelect>
            </InputContainer>
            {errors.fulfillmentChangeReason && (
              <p className="text-sm text-red-600">
                {errors.fulfillmentChangeReason.message}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <InputLabel>Additional comments</InputLabel>
            <ChatInput
              value={fulfillmentChangeReason.message}
              setValue={(message) => {
                onChange({ ...fulfillmentChangeReason, message });
              }}
              placeholder={"Additional comments"}
              maxLength={900}
              rows={4}
              maxRows={8}
            />
            {errors.fulfillmentChangeReasonMessage && (
              <p className="text-sm text-red-600">
                {errors.fulfillmentChangeReasonMessage.message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
